import { buildGraphqlAuthInput, ON_CALL_AUTH_GRAPHQL_FIELDS } from '@/utils/onCallAuthGraphql'

function pickString(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return ''
  for (const key of keys) {
    const value = raw[key]
    if (value != null && String(value).trim()) return String(value).trim()
  }
  return ''
}

function normalizeFieldList(value) {
  if (!value) return []
  if (Array.isArray(value)) return value.map((item) => String(item)).filter(Boolean)
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed.map((item) => String(item)).filter(Boolean) : []
    } catch {
      return value.split(',').map((item) => item.trim()).filter(Boolean)
    }
  }
  return []
}

function stripSendToBracketWrappers(text = '') {
  let value = String(text ?? '').trim()
  let changed = true

  while (changed && value.length > 1) {
    changed = false
    const wrapped =
      (value.startsWith('[[') && value.endsWith(']]'))
      || (value.startsWith('[') && value.endsWith(']'))

    if (!wrapped) break

    const inner = value.startsWith('[[')
      ? value.slice(2, -2).trim()
      : value.slice(1, -1).trim()

    if (inner && inner !== value) {
      value = inner
      changed = true
    }
  }

  return value
}

export function normalizeSendToList(value) {
  const results = []
  const seen = new Set()

  const add = (item) => {
    const cleaned = stripSendToBracketWrappers(String(item ?? '').trim())
    if (!cleaned || seen.has(cleaned)) return
    seen.add(cleaned)
    results.push(cleaned)
  }

  const walk = (input) => {
    if (input == null || input === '') return

    if (Array.isArray(input)) {
      input.forEach((item) => walk(item))
      return
    }

    let text = String(input).trim()
    if (!text) return

    text = stripSendToBracketWrappers(text)
    if (!text) return

    if (text.startsWith('[')) {
      try {
        walk(JSON.parse(text))
        return
      } catch {
        // Fall through to delimiter split.
      }
    }

    text.split(/[,;\n]+/).forEach((part) => add(part))
  }

  walk(value)
  return results
}

export function usesArraySendTo(actionType) {
  return actionType === 'CALL_REDIRECT' || actionType === 'POST_RESPONSE'
}

function normalizeNotificationChannel(raw) {
  if (!raw || typeof raw !== 'object') return null

  const sendToList = normalizeSendToList(raw.send_to ?? raw.sendTo)

  return {
    enabled: Boolean(raw.enabled),
    send_to: sendToList.length ? sendToList.join(', ') : pickString(raw, 'send_to', 'sendTo'),
    send_to_list: sendToList,
    subject: pickString(raw, 'subject'),
    url: pickString(raw, 'url'),
    template: pickString(raw, 'template'),
    fields: normalizeFieldList(raw.fields),
  }
}

function parseAuthHeadersMap(value) {
  if (!value) return {}
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.fromEntries(
      Object.entries(value).filter(([key]) => key && !String(key).startsWith('__')),
    )
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return {}
    try {
      const parsed = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.fromEntries(
          Object.entries(parsed).filter(([key]) => key && !String(key).startsWith('__')),
        )
      }
    } catch {
      return {}
    }
  }
  return {}
}

function stringifyAuthHeaders(headers = {}) {
  const clean = parseAuthHeadersMap(headers)
  if (!Object.keys(clean).length) return undefined
  try {
    return JSON.stringify(clean)
  } catch {
    return undefined
  }
}

function buildCustomHeaderAuthPayload(auth = {}) {
  let headers = parseAuthHeadersMap(auth.headers)
  const headerKey = String(auth.key || '').trim()
  if (headerKey) {
    headers = {
      ...headers,
      [headerKey]: String(auth.value ?? ''),
    }
  }

  const headersJson = stringifyAuthHeaders(headers)
  const payload = { type: 'custom_header' }
  if (headersJson) payload.headers = headersJson
  return payload
}

function webhookAuthFromRaw(authRaw = {}) {
  const type = pickString(authRaw, 'type') || 'none'
  const auth = {
    type,
    key: pickString(authRaw, 'key'),
    value: pickString(authRaw, 'value'),
    token: pickString(authRaw, 'token'),
    username: pickString(authRaw, 'username'),
    password: pickString(authRaw, 'password'),
    token_url: pickString(authRaw, 'token_url', 'tokenUrl'),
    client_id: pickString(authRaw, 'client_id', 'clientId'),
    client_secret: pickString(authRaw, 'client_secret', 'clientSecret'),
    scope: pickString(authRaw, 'scope'),
    headers: parseAuthHeadersMap(authRaw.headers),
  }

  if (type === 'custom_header' && !auth.key) {
    const entries = Object.entries(auth.headers).filter(([key]) => key)
    if (entries.length) {
      auth.key = entries[0][0]
      auth.value = String(entries[0][1] ?? '')
    }
  }

  if (type === 'hmac') {
    auth.key = pickString(authRaw, 'key', 'header') || auth.key
    auth.value = pickString(authRaw, 'value', 'secret') || auth.value
    auth.scope = pickString(authRaw, 'scope', 'algorithm') || auth.scope || 'sha256'
  } else if (type === 'jwt') {
    auth.token = pickString(authRaw, 'secret', 'token') || auth.token
  } else if (type === 'query_api_key') {
    auth.key = pickString(authRaw, 'param', 'key') || auth.key
    auth.value = pickString(authRaw, 'key', 'value') || auth.value
  }

  return auth
}

function normalizeWebhookChannel(raw) {
  if (!raw || typeof raw !== 'object') return null

  const authRaw = raw.auth || {}

  return {
    enabled: Boolean(raw.enabled),
    url: pickString(raw, 'url'),
    fields: normalizeFieldList(raw.fields),
    auth: webhookAuthFromRaw(authRaw),
  }
}

export const ON_CALL_NOTIFICATION_ICONS = [
  { key: 'email', icon: 'mdi-email-outline', label: 'Email' },
  { key: 'whatsapp', icon: 'mdi-whatsapp', label: 'WhatsApp' },
  { key: 'webhook', icon: 'mdi-webhook', label: 'Webhook' },
]

export function getEnabledOnCallNotificationChannels(notifications) {
  if (!notifications || typeof notifications !== 'object') return []

  return ON_CALL_NOTIFICATION_ICONS.filter(
    (row) => Boolean(notifications[row.key]?.enabled),
  )
}

export function formatOnCallNotificationsSummary(notifications) {
  const channels = getEnabledOnCallNotificationChannels(notifications)
  return channels.length ? channels.map((row) => row.label).join(', ') : '—'
}

export function normalizeOnCallNotifications(raw) {
  if (!raw || typeof raw !== 'object') return null

  const email = normalizeNotificationChannel(raw.email)
  const whatsapp = normalizeNotificationChannel(raw.whatsapp)
  const webhook = normalizeWebhookChannel(raw.webhook)

  if (!email && !whatsapp && !webhook) return null

  return {
    email: email || defaultNotificationChannel('email'),
    whatsapp: whatsapp || defaultNotificationChannel('whatsapp'),
    webhook: webhook || defaultNotificationChannel('webhook'),
  }
}

export function defaultNotificationChannel(type) {
  if (type === 'email') {
    return {
      enabled: false,
      send_to_mode: 'caller',
      send_to: '{caller_email}',
      custom_send_to: '',
      send_to_list: [],
      subject: '',
      template: '',
      fields: [],
    }
  }

  if (type === 'whatsapp') {
    return {
      enabled: false,
      send_to: '{caller_number}',
      send_to_list: [],
      template: '',
      fields: [],
    }
  }

  return {
    enabled: false,
    url: '',
    fields: [],
    auth: {
      type: 'none',
      key: '',
      value: '',
      token: '',
      username: '',
      password: '',
      token_url: '',
      client_id: '',
      client_secret: '',
      scope: '',
    },
  }
}

export const EMAIL_SEND_TO_OPTIONS = [
  { value: 'caller', title: "Caller's Email", sendTo: '{caller_email}' },
  { value: 'custom', title: 'Custom Email', sendTo: '' },
]

export function resolveEmailSendTo(email = {}) {
  if (email.send_to_mode === 'custom') {
    return String(email.custom_send_to || email.send_to || '').trim()
  }
  return '{caller_email}'
}

export function emailSendToModeFromValue(sendTo) {
  const value = String(sendTo || '').trim()
  if (!value || value === '{caller_email}') {
    return { send_to_mode: 'caller', custom_send_to: '' }
  }
  return { send_to_mode: 'custom', custom_send_to: value }
}

export function notificationFieldRows(responseFields = [], record = null) {
  if (Array.isArray(responseFields) && responseFields.length) {
    return responseFields
      .map((row) => ({
        path: String(row?.path ?? row?.fullPath ?? '').trim(),
        value: String(row?.value ?? '').trim(),
      }))
      .filter((row) => row.path)
  }

  const paths = new Set()
  const notifications = record?.notifications || {}
  ;['email', 'whatsapp', 'webhook'].forEach((key) => {
    const fields = notifications[key]?.fields
    if (Array.isArray(fields)) {
      fields.forEach((field) => {
        const path = String(field || '').trim()
        if (path) paths.add(path)
      })
    }
  })

  return [...paths].map((path) => ({ path, value: '' }))
}

export function defaultEmailSubject(actionName = '') {
  const name = String(actionName || 'Action').trim()
  return `✅ ${name} Confirmed`
}

export function defaultWhatsappTemplate(actionName = '') {
  const name = String(actionName || 'Action').trim()
  return `✅ *${name}*\nHi {caller_name},`
}

export function defaultNotificationsForm() {
  return {
    email: defaultNotificationChannel('email'),
    whatsapp: defaultNotificationChannel('whatsapp'),
    webhook: defaultNotificationChannel('webhook'),
  }
}

export function responseFieldPaths(responseFields = []) {
  return responseFields
    .map((row) => String(row?.path ?? row?.fullPath ?? '').trim())
    .filter(Boolean)
}

function suggestionPathsForChannel(channelKey, suggestions = {}) {
  const key = `${channelKey}_fields`
  return normalizeFieldList(suggestions?.[key])
}

function rowsForPaths(paths, responseFields = [], record = null) {
  const normalizedPaths = normalizeFieldList(paths)
  if (!normalizedPaths.length) return []

  const allRows = notificationFieldRows(responseFields, record)
  const rowByPath = new Map(allRows.map((row) => [row.path, row]))

  return normalizedPaths.map((path) => rowByPath.get(path) || { path, value: '' })
}

export function channelNotificationFieldRows(
  channelKey,
  responseFields = [],
  suggestions = {},
  record = null,
) {
  if (Array.isArray(responseFields) && responseFields.length) {
    return notificationFieldRows(responseFields, record)
  }

  const suggestedPaths = suggestionPathsForChannel(channelKey, suggestions)
  if (suggestedPaths.length) {
    return rowsForPaths(suggestedPaths, responseFields, record)
  }

  const savedPaths = normalizeFieldList(record?.notifications?.[channelKey]?.fields)
  if (savedPaths.length) {
    return rowsForPaths(savedPaths, responseFields, record)
  }

  return []
}

function resolveChannelSelectedFields(channelKey, existingFields, suggestions = {}) {
  const suggested = suggestionPathsForChannel(channelKey, suggestions)
  const saved = normalizeFieldList(existingFields)

  if (suggested.length) {
    if (saved.length) {
      return saved.filter((field) => suggested.includes(field))
    }
    return [...suggested]
  }

  return saved
}

import { extractTemplateFieldPaths } from '@/utils/notificationTemplateEditor'

function defaultFieldsFromTestTemplates(suggestions = {}, responseFields = []) {
  const templatePaths = extractTemplateFieldPaths(
    suggestions.email_template,
    suggestions.whatsapp_template,
    suggestions.email_subject,
  )

  const available = new Set(responseFieldPaths(responseFields))
  const matched = templatePaths.filter((path) => available.has(path))

  if (matched.length) return matched

  const preferred = ['topic', 'start_time', 'duration', 'id', 'password', 'join_url', 'timezone']
  const fromPreferred = preferred.filter((path) => available.has(path))
  if (fromPreferred.length) return fromPreferred

  return responseFieldPaths(responseFields).slice(0, 8)
}

export function buildNotificationsForm(record, suggestions = {}, responseFields = []) {
  const form = defaultNotificationsForm()
  const existing = normalizeOnCallNotifications(record?.notifications)
  const actionName = record?.action_name || ''
  const arraySendTo = usesArraySendTo(record?.action_type)

  if (existing?.email) {
    if (arraySendTo) {
      const { send_to: _sendTo, ...restEmail } = existing.email
      form.email = {
        ...form.email,
        ...restEmail,
        send_to_list: normalizeSendToList(existing.email.send_to_list ?? existing.email.send_to),
      }
    } else {
      const emailMode = emailSendToModeFromValue(existing.email.send_to)
      form.email = {
        ...form.email,
        ...existing.email,
        ...emailMode,
        send_to_list: normalizeSendToList(
          existing.email.send_to_list ?? existing.email.send_to,
        ),
      }
    }
  }

  if (existing?.whatsapp) {
    const { send_to: _sendTo, ...restWhatsapp } = existing.whatsapp
    form.whatsapp = {
      ...form.whatsapp,
      ...restWhatsapp,
      send_to_list: normalizeSendToList(
        existing.whatsapp.send_to_list ?? existing.whatsapp.send_to,
      ),
    }
  }
  if (existing?.webhook) {
    form.webhook = {
      ...form.webhook,
      ...existing.webhook,
      auth: webhookAuthFromRaw(existing.webhook.auth || {}),
    }
  }

  if (suggestions.email_subject) {
    form.email.subject = suggestions.email_subject
  }

  if (suggestions.email_template) {
    form.email.template = suggestions.email_template
    if (!existing?.email?.enabled) {
      form.email.enabled = true
    }
  }

  if (suggestions.whatsapp_template) {
    form.whatsapp.template = suggestions.whatsapp_template
    if (!existing?.whatsapp?.enabled) {
      form.whatsapp.enabled = true
    }
  }

  if (!form.email.subject) {
    form.email.subject = arraySendTo
      ? defaultRedirectEmailSubject(actionName)
      : defaultEmailSubject(actionName)
  }

  if (!form.whatsapp.template && !suggestions.whatsapp_template) {
    form.whatsapp.template = arraySendTo
      ? defaultRedirectWhatsappTemplate()
      : defaultWhatsappTemplate(actionName)
  }

  const defaultFields = defaultFieldsFromTestTemplates(suggestions, responseFields)

  if (!form.email.fields.length) {
    form.email.fields = resolveChannelSelectedFields(
      'email',
      existing?.email?.fields,
      suggestions,
    )
    if (!form.email.fields.length && defaultFields.length) {
      form.email.fields = [...defaultFields]
    }
  }

  if (!form.whatsapp.fields.length) {
    form.whatsapp.fields = resolveChannelSelectedFields(
      'whatsapp',
      existing?.whatsapp?.fields,
      suggestions,
    )
    if (!form.whatsapp.fields.length && defaultFields.length) {
      form.whatsapp.fields = [...defaultFields]
    }
  }

  if (!form.webhook.fields.length) {
    form.webhook.fields = resolveChannelSelectedFields(
      'webhook',
      existing?.webhook?.fields,
      suggestions,
    )
    if (!form.webhook.fields.length && defaultFields.length) {
      form.webhook.fields = [...defaultFields]
    }
  }

  ;['email', 'whatsapp', 'webhook'].forEach((channelKey) => {
    const suggested = suggestionPathsForChannel(channelKey, suggestions)
    if (!suggested.length) return
    const selected = normalizeFieldList(form[channelKey].fields)
      .filter((field) => suggested.includes(field))
    form[channelKey].fields = selected.length ? selected : [...suggested]
  })

  return form
}

function pickAllowedFields(source, allowedFields) {
  if (!source || typeof source !== 'object') return undefined

  const allowed = new Set(allowedFields)
  const picked = {}

  Object.entries(source).forEach(([key, value]) => {
    if (!allowed.has(key) || value === undefined || value === null) return
    picked[key] = value
  })

  return Object.keys(picked).length ? picked : undefined
}

function buildWebhookAuthPayload(auth = {}) {
  return pickAllowedFields(buildGraphqlAuthInput(auth), ON_CALL_AUTH_GRAPHQL_FIELDS) || { type: 'none' }
}

function buildChannelPayload(channel, type, options = {}) {
  const { arraySendTo = false } = options
  const payload = {
    enabled: Boolean(channel?.enabled),
  }

  if (type === 'webhook') {
    if (channel.url) payload.url = String(channel.url).trim()
    payload.auth = buildWebhookAuthPayload(channel.auth)
  } else if (type === 'email') {
    const recipients = normalizeSendToList(channel.send_to_list ?? channel.send_to)
    if (recipients.length === 1) {
      payload.send_to = recipients[0]
    } else if (recipients.length > 1) {
      payload.send_to = recipients
    } else if (arraySendTo) {
      // array mode with no recipients — omit send_to
    } else {
      const sendTo = resolveEmailSendTo(channel)
      if (sendTo) payload.send_to = sendTo
    }
    if (channel.subject) payload.subject = String(channel.subject).trim()
    if (channel.template) payload.template = String(channel.template).trim()
  } else {
    if (arraySendTo) {
      const recipients = normalizeSendToList(channel.send_to_list ?? channel.send_to)
      if (recipients.length) payload.send_to = recipients
    } else {
      const recipients = normalizeSendToList(channel.send_to_list ?? channel.send_to)
      if (recipients.length === 1) {
        payload.send_to = recipients[0]
      } else if (recipients.length > 1) {
        payload.send_to = recipients
      } else if (channel.send_to) {
        payload.send_to = String(channel.send_to).trim()
      }
    }
    if (channel.template) payload.template = String(channel.template).trim()
  }

  if (Array.isArray(channel.fields) && channel.fields.length) {
    payload.fields = channel.fields.map((item) => String(item).trim()).filter(Boolean)
  }

  return payload
}

/** Payload for create/update onCallAction (notifications field). */
export function buildNotificationsInput(notificationsForm, options = {}) {
  if (!notificationsForm) return undefined
  const arraySendTo = Boolean(options.arraySendTo)

  return {
    email: buildChannelPayload(notificationsForm.email, 'email', { arraySendTo }),
    whatsapp: buildChannelPayload(notificationsForm.whatsapp, 'whatsapp', { arraySendTo }),
    webhook: buildChannelPayload(notificationsForm.webhook, 'webhook'),
  }
}

/** Payload for updateOnCallAction (notifications field). */
export function buildUpdateOnCallNotificationsInput(actionId, form, options = {}) {
  return {
    action_id: String(actionId ?? '').trim(),
    notifications: buildNotificationsInput(form, options),
  }
}

const CHANNEL_LABELS = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  webhook: 'Webhook',
}

export function isValidWebhookUrl(value = '') {
  const text = String(value ?? '').trim()
  if (!text) return false

  try {
    const url = new URL(text)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export function validateWebhookAuth(auth = {}) {
  const type = String(auth?.type || 'none').trim()
  if (!type || type === 'none') return ''

  if (type === 'api_key' || type === 'custom_header') {
    if (!String(auth.key || '').trim()) return 'Enter the webhook auth header name.'
    if (!String(auth.value || '').trim()) return 'Enter the webhook auth header value.'
    return ''
  }

  if (type === 'bearer') {
    if (!String(auth.token || '').trim()) return 'Enter the webhook bearer token.'
    return ''
  }

  if (type === 'basic') {
    if (!String(auth.username || '').trim()) return 'Enter the webhook basic auth username.'
    if (!String(auth.password || '').trim()) return 'Enter the webhook basic auth password.'
    return ''
  }

  if (type === 'oauth2_client') {
    if (!String(auth.token_url || '').trim()) return 'Enter the webhook OAuth token URL.'
    if (!isValidWebhookUrl(auth.token_url)) return 'Enter a valid webhook OAuth token URL.'
    if (!String(auth.client_id || '').trim()) return 'Enter the webhook OAuth client ID.'
    if (!String(auth.client_secret || '').trim()) return 'Enter the webhook OAuth client secret.'
    return ''
  }

  if (type === 'hmac') {
    if (!String(auth.key || auth.header || '').trim()) return 'Enter the HMAC signature header name.'
    if (!String(auth.value || auth.secret || '').trim()) return 'Enter the HMAC secret.'
    return ''
  }

  if (type === 'jwt') {
    if (!String(auth.token || auth.secret || '').trim()) return 'Enter the JWT secret.'
    return ''
  }

  if (type === 'query_api_key') {
    if (!String(auth.key || auth.param || '').trim()) return 'Enter the query parameter name.'
    if (!String(auth.value || '').trim()) return 'Enter the API key value.'
    return ''
  }

  return ''
}

export function validateNotificationsFormDetails(form, options = {}) {
  const { schemaAligned = false, arraySendTo = false } = options
  const fields = {}
  let message = ''

  const setError = (key, text) => {
    if (!fields[key]) fields[key] = text
    if (!message) message = text
  }

  if (schemaAligned) {
    if (form.webhook?.enabled) {
      const url = String(form.webhook.url || '').trim()
      if (url && !isValidWebhookUrl(url)) {
        setError(
          'webhook_url',
          'Enter a valid webhook URL starting with http:// or https://.',
        )
      }
    }

    return {
      valid: !message,
      message,
      fields,
    }
  }

  if (form.email?.enabled && arraySendTo) {
    if (!normalizeSendToList(form.email.send_to_list).length) {
      setError('email_send_to', 'Add at least one email recipient.')
    }
  } else if (form.email?.enabled && form.email.send_to_mode === 'custom') {
    const recipients = normalizeSendToList(form.email.send_to_list)
    if (!recipients.length && !String(form.email.custom_send_to || '').trim()) {
      setError('email_send_to', 'Add at least one email recipient.')
    }
  }

  if (form.whatsapp?.enabled && arraySendTo) {
    if (!normalizeSendToList(form.whatsapp.send_to_list).length) {
      setError('whatsapp_send_to', 'Add at least one WhatsApp recipient.')
    }
  } else if (form.whatsapp?.enabled) {
    const recipients = normalizeSendToList(form.whatsapp.send_to_list ?? form.whatsapp.send_to)
    if (!recipients.length) {
      setError('whatsapp_send_to', 'Add at least one WhatsApp recipient.')
    }
  }

  const enabledChannels = ['email', 'whatsapp', 'webhook'].filter(
    (key) => form[key]?.enabled,
  )

  if (!enabledChannels.length) {
    message = 'Enable at least one notification channel.'
    return { valid: false, message, fields }
  }

  if (form.webhook?.enabled) {
    const url = String(form.webhook.url || '').trim()
    if (!url) {
      setError('webhook_url', 'Webhook URL is required when webhook is enabled.')
    } else if (!isValidWebhookUrl(url)) {
      setError('webhook_url', 'Enter a valid webhook URL starting with http:// or https://.')
    }

    const authError = validateWebhookAuth(form.webhook.auth)
    if (authError) {
      setError('webhook_auth', authError)
    }
  }

  for (const key of enabledChannels) {
    if (!form[key]?.fields?.length) {
      setError(`${key}_fields`, `Select at least one response field for ${CHANNEL_LABELS[key]}.`)
    }
  }

  return {
    valid: !message,
    message,
    fields,
  }
}

export function validateNotificationsForm(form) {
  return validateNotificationsFormDetails(form).message
}

export function defaultRedirectEmailSubject(actionName = '') {
  const name = String(actionName || 'Support Team').trim()
  return `Your Call Has Been Transferred to Our ${name}`
}

export function defaultRedirectWhatsappTemplate() {
  return `*👋 Hello! Your call has been successfully transferred.*

*📞 Transfer Number:* {transfer_number}
*💬 Message:* {message}`
}

export const REDIRECT_NOTIFICATION_CHANNELS = [
  {
    key: 'email',
    title: 'Email',
    desc: 'Send a confirmation email after the call.',
    icon: 'mdi-email-outline',
    accent: '#3b82f6',
    glow: 'rgba(59, 130, 246, 0.22)',
  },
  {
    key: 'whatsapp',
    title: 'WhatsApp',
    desc: 'Send a WhatsApp summary with collected answers.',
    icon: 'mdi-whatsapp',
    accent: '#22c55e',
    glow: 'rgba(34, 197, 94, 0.22)',
  },
  {
    key: 'webhook',
    title: 'Webhook',
    desc: 'Saved inside notifications.webhook with URL, auth, and fields.',
    icon: 'mdi-link-variant',
    accent: '#8b5cf6',
    glow: 'rgba(139, 92, 246, 0.22)',
  },
]

export const NOTIFICATION_CHANNELS = [
  {
    key: 'email',
    title: 'Email',
    desc: 'Send confirmation email to caller',
    icon: 'mdi-email-outline',
    accent: '#3b82f6',
    glow: 'rgba(59, 130, 246, 0.22)',
  },
  {
    key: 'whatsapp',
    title: 'WhatsApp',
    desc: "Send WhatsApp message to caller's number",
    icon: 'mdi-whatsapp',
    accent: '#22c55e',
    glow: 'rgba(34, 197, 94, 0.22)',
  },
  {
    key: 'webhook',
    title: 'Webhook',
    desc: 'POST selected response fields to your endpoint',
    icon: 'mdi-link-variant',
    accent: '#8b5cf6',
    glow: 'rgba(139, 92, 246, 0.22)',
  },
]

export const OUTGOING_NOTIFICATION_CHANNELS = [
  {
    key: 'email',
    title: 'Email',
    desc: 'Notify a manager or team inbox when the call completes',
    icon: 'mdi-email-outline',
    accent: '#3b82f6',
    glow: 'rgba(59, 130, 246, 0.22)',
  },
  {
    key: 'whatsapp',
    title: 'WhatsApp',
    desc: 'Send a WhatsApp summary when the call completes',
    icon: 'mdi-whatsapp',
    accent: '#22c55e',
    glow: 'rgba(34, 197, 94, 0.22)',
  },
  {
    key: 'webhook',
    title: 'Webhook',
    desc: 'POST selected call fields to a notification endpoint',
    icon: 'mdi-link-variant',
    accent: '#8b5cf6',
    glow: 'rgba(139, 92, 246, 0.22)',
  },
]

function humanizeFieldKey(key = '') {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export function outgoingNotificationFieldKeys(requiredFieldRows = []) {
  const keys = ['customer_name', 'service_name']

  ;(requiredFieldRows || []).forEach((row) => {
    const key = String(row?.key ?? row?.field ?? '').trim()
    if (key) keys.push(key)
  })

  return [...new Set(keys)]
}

export function outgoingNotificationFieldOptions(requiredFieldRows = []) {
  return outgoingNotificationFieldKeys(requiredFieldRows).map((path) => ({
    path,
    value: '',
  }))
}

function resolveOrgNotificationEmail(org = {}) {
  const direct = pickString(org, 'org_contact_email', 'orgContactEmail')
  if (direct) return direct

  const addresses = org.email_addresses || org.additional_email_addresses || []
  if (Array.isArray(addresses)) {
    const first = addresses.map((item) => String(item || '').trim()).find(Boolean)
    if (first) return first
  }

  return ''
}

function resolveOrgNotificationWhatsapp(org = {}) {
  const numbers = org.whatsapp_numbers || org.additional_whatsapp_numbers || []
  const first = Array.isArray(numbers)
    ? String(numbers.find(Boolean) || '').trim()
    : ''

  const code = pickString(org, 'org_country_code', 'orgCountryCode').replace(/\D/g, '')

  if (first) {
    if (first.startsWith('+')) return first
    const digits = first.replace(/\D/g, '')
    return code ? `+${code}${digits}` : `+${digits}`
  }

  const contact = pickString(org, 'org_contact_number', 'orgContactNumber').replace(/\D/g, '')
  if (contact && code) return `+${code}${contact}`
  if (contact) return `+${contact}`

  return ''
}

export function buildDefaultOutgoingEmailTemplate(fieldKeys = []) {
  const detailKeys = fieldKeys.filter(
    (key) => key && !['customer_name', 'service_name'].includes(key),
  )

  if (!detailKeys.length) {
    return 'Call completed with {customer_name}.'
  }

  const details = detailKeys
    .map((key) => `${humanizeFieldKey(key)}: {${key}}`)
    .join('. ')

  return `Call completed with {customer_name}. ${details}.`
}

export function buildDefaultOutgoingWhatsappTemplate(fieldKeys = []) {
  const detailKeys = fieldKeys.filter(
    (key) => key && !['customer_name', 'service_name'].includes(key),
  )

  if (detailKeys.length >= 2) {
    const second = detailKeys[1]
    const last = detailKeys[detailKeys.length - 1]
    return `Call done with {customer_name}. ${humanizeFieldKey(second)}: {${second}}. ${humanizeFieldKey(last)}: {${last}}.`
  }

  if (detailKeys.length === 1) {
    return `Call done with {customer_name}. ${humanizeFieldKey(detailKeys[0])}: {${detailKeys[0]}}.`
  }

  return 'Call done with {customer_name}.'
}

export function buildDefaultOutgoingNotificationsForm({
  actionName = '',
  requiredFieldRows = [],
  org = {},
  existing = null,
} = {}) {
  const form = existing
    ? {
        email: { ...defaultNotificationChannel('email'), ...existing.email },
        whatsapp: { ...defaultNotificationChannel('whatsapp'), ...existing.whatsapp },
        webhook: {
          ...defaultNotificationChannel('webhook'),
          ...existing.webhook,
          auth: webhookAuthFromRaw(existing.webhook?.auth || {}),
        },
      }
    : defaultNotificationsForm()

  const fieldKeys = outgoingNotificationFieldKeys(requiredFieldRows)
  const collectKeys = fieldKeys.filter((key) => !['customer_name', 'service_name'].includes(key))
  const emailFields = collectKeys.length ? [...collectKeys] : [...fieldKeys]
  const whatsappFields =
    collectKeys.length >= 2
      ? [collectKeys[1], collectKeys[collectKeys.length - 1]].filter(Boolean)
      : collectKeys.slice(0, 2)

  const hasExisting = Boolean(
    existing?.email?.enabled ||
      existing?.whatsapp?.enabled ||
      existing?.webhook?.enabled ||
      existing?.email?.template ||
      existing?.whatsapp?.template,
  )

  if (!hasExisting) {
    form.email = {
      ...form.email,
      enabled: true,
      send_to_mode: 'custom',
      custom_send_to: resolveOrgNotificationEmail(org),
      subject: actionName
        ? `${actionName} — {customer_name}`
        : 'Service Call Completed — {customer_name}',
      template: buildDefaultOutgoingEmailTemplate(fieldKeys),
      fields: emailFields,
    }
    form.whatsapp = {
      ...form.whatsapp,
      enabled: true,
      send_to: resolveOrgNotificationWhatsapp(org),
      template: buildDefaultOutgoingWhatsappTemplate(fieldKeys),
      fields: whatsappFields.length ? whatsappFields : collectKeys.slice(0, 2),
    }
    form.webhook = {
      ...form.webhook,
      enabled: false,
      url: '',
      fields: [],
      auth: { ...webhookAuthFromRaw(form.webhook.auth), type: 'none' },
    }
  } else if (existing?.email) {
    form.email = {
      ...form.email,
      ...emailSendToModeFromValue(existing.email.send_to),
    }
  }

  return form
}

export function syncOutgoingNotificationsFromForm(form, org = {}) {
  if (!form) return form

  if (!form.notifications) {
    form.notifications = buildDefaultOutgoingNotificationsForm({
      actionName: form.action_name,
      requiredFieldRows: form.config?.required_field_rows,
      org,
    })
    return form
  }

  const fieldKeys = outgoingNotificationFieldKeys(form.config?.required_field_rows)
  const collectKeys = fieldKeys.filter((key) => !['customer_name', 'service_name'].includes(key))

  ;['email', 'whatsapp', 'webhook'].forEach((channelKey) => {
    const channel = form.notifications[channelKey]
    if (!channel?.enabled) return

    const currentFields = normalizeFieldList(channel.fields)
    const valid = currentFields.filter((field) => fieldKeys.includes(field))

    if (channelKey === 'email') {
      channel.fields = valid.length ? valid : collectKeys.length ? [...collectKeys] : [...fieldKeys]
      if (!String(channel.template || '').trim()) {
        channel.template = buildDefaultOutgoingEmailTemplate(fieldKeys)
      }
      if (!String(channel.subject || '').trim()) {
        channel.subject = form.action_name
          ? `${form.action_name} — {customer_name}`
          : 'Service Call Completed — {customer_name}'
      }
    }

    if (channelKey === 'whatsapp') {
      const defaultFields =
        collectKeys.length >= 2
          ? [collectKeys[1], collectKeys[collectKeys.length - 1]]
          : collectKeys.slice(0, 2)
      channel.fields = valid.length ? valid : defaultFields
      if (!String(channel.template || '').trim()) {
        channel.template = buildDefaultOutgoingWhatsappTemplate(fieldKeys)
      }
    }

    if (channelKey === 'webhook') {
      channel.fields = valid.length ? valid : collectKeys
    }
  })

  if (form.notifications.email?.send_to_mode === 'custom') {
    if (!String(form.notifications.email.custom_send_to || '').trim()) {
      form.notifications.email.custom_send_to = resolveOrgNotificationEmail(org)
    }
  }

  if (
    form.notifications.whatsapp?.enabled &&
    !String(form.notifications.whatsapp.send_to || '').trim()
  ) {
    form.notifications.whatsapp.send_to = resolveOrgNotificationWhatsapp(org)
  }

  return form
}
